/** Reads order history over HTTP and exposes live events with subscription ACKs for HTTP reconciliation. */
import { toAssetTypeParam } from "../asset-type.types";
import { BaseReader } from "../base-reader";
import {
  UserOrderHistoryRequestArgs,
  UserOrdersSchema,
  UserOrdersWsMessage,
  UserOrdersWsMessageSchema,
} from "./user-order-history.types";

export class UserOrderHistoryReader extends BaseReader {
  async getByAddr({
    subAddr,
    limit,
    offset,
    startTimestamp,
    endTimestamp,
    sortDir,
    assetType = "perp",
    fetchOptions,
  }: UserOrderHistoryRequestArgs) {
    const queryParams: Record<string, string> = {
      account: subAddr,
    };
    if (limit !== undefined) queryParams.limit = limit.toString();
    if (offset !== undefined) queryParams.offset = offset.toString();
    if (startTimestamp !== undefined) queryParams.start_timestamp = startTimestamp.toString();
    if (endTimestamp !== undefined) queryParams.end_timestamp = endTimestamp.toString();
    if (sortDir !== undefined) queryParams.sort_dir = sortDir;
    const assetTypeParam = toAssetTypeParam(assetType);
    if (assetTypeParam !== undefined) queryParams.asset_type = assetTypeParam;

    const response = await this.getRequest({
      schema: UserOrdersSchema,
      url: `${this.deps.config.tradingHttpUrl}/api/v1/order_history`,
      queryParams,
      options: fetchOptions,
    });

    return response.data;
  }

  /**
   * Subscribe to user order updates
   * @param subAddr The subaccount address of the user to subscribe to
   * @param onData Callback function for received user order data
   * @param onSubscribed Called after subscription acknowledgment; use it to reconcile non-replaying history over HTTP.
   * @returns A function to unsubscribe from the user order updates
   */
  subscribeByAddr(
    subAddr: string,
    onData: (data: UserOrdersWsMessage) => void,
    onSubscribed?: () => void,
  ) {
    const topic = `order_updates:${subAddr}`;

    return this.deps.ws.subscribe(topic, UserOrdersWsMessageSchema, onData, onSubscribed);
  }
}
